import React, { useState, useEffect } from 'react';
import SideMenu from "../../component/SideMenu";
import Topbar from "../../component/Topbar";
import SettingNav from './SettingNav';
import { LuTicket, LuHeadphones, LuHistory, LuPlus } from "react-icons/lu";
import "../../asset/css/profile-info.css";
import { baseurl } from '../../baseurl';
import axios from 'axios';
import Swal from 'sweetalert2';
import AddTicket from './AddTicket';

const SupportTicket = () => {
    const userToken = localStorage.getItem('userToken');
    const [tickets, setTickets] = useState([]);
    const [loadingTickets, setLoadingTickets] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [fetchingPrefs, setFetchingPrefs] = useState(true);

    // Preferences state
    const [config, setConfig] = useState({
        contactMethod: "Email",
        businessHours: "9 AM - 5 PM (Local Time)",
        autoEscalate: "24 hours",
        emailUpdates: true
    });

    // 1. Fetch Tickets from API
    const fetchTickets = async () => {
        try {
            setLoadingTickets(true);
            const res = await axios.get(`${baseurl}getTicketsByUser`, {
                headers: { Authorization: `Bearer ${userToken}` }
            });
            if (res.data.success) {
                setTickets(res.data.tickets);
            }
        } catch (err) {
            console.error("Error fetching tickets:", err);
        } finally {
            // Artificial delay to show skeleton (optional)
            setTimeout(() => setLoadingTickets(false), 800);
        }
    };

    // 2. Fetch User Preferences
    useEffect(() => {
        const fetchSettings = async () => {
            try {
                setFetchingPrefs(true);
                const res = await axios.get(`${baseurl}getUserPreferences`, {
                    headers: { Authorization: `Bearer ${userToken}` }
                });
                if (res.data.success && res.data.data.ticket_preference) {
                    setConfig(prev => ({
                        ...prev,
                        ...res.data.data.ticket_preference
                    }));
                }
            } catch (err) {
                console.error("Fetch Error:", err);
            } finally {
                setFetchingPrefs(false);
            }
        };
        fetchSettings();
        fetchTickets();
    }, [userToken]);

    const handlePrefChange = (key, value) => {
        setConfig(prev => ({ ...prev, [key]: value }));
    };

    const saveSettings = async () => {
        setIsLoading(true);
        try {
            const res = await axios.put(`${baseurl}updateUserPreferences`, {
                column: 'ticket_preference',
                data: config 
            }, {
                headers: { Authorization: `Bearer ${userToken}` }
            });

            if (res.data.success) {
                Swal.fire({ 
                    icon: 'success', 
                    title: 'Preferences updated', 
                    toast: true, 
                    position: 'top-end', 
                    showConfirmButton: false, 
                    timer: 3000 
                });
            }
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Update failed', toast: true, position: 'top-end', showConfirmButton: false, timer: 3000 });
        } finally {
            setIsLoading(false);
        }
    };

    // --- SKELETON COMPONENTS ---
    const TicketSkeleton = () => (
        <div className="ticket-box skeleton-wrapper">
            <div className="d-flex justify-content-between mb-2">
                <div className="skeleton skeleton-title"></div>
                <div className="skeleton skeleton-badge"></div>
            </div>
            <div className="skeleton skeleton-text"></div>
            <div className="skeleton skeleton-text short"></div>
            <div className="d-flex justify-content-between mt-3">
                <div className="skeleton skeleton-meta"></div>
                <div className="skeleton skeleton-meta"></div>
            </div>
        </div>
    );

    const PreferenceSkeleton = () => (
        <div className="form-body">
            {[1, 2, 3].map(i => (
                <div key={i} className="mb-4">
                    <div className="skeleton skeleton-label mb-2"></div>
                    <div className="skeleton skeleton-input"></div>
                </div>
            ))}
        </div>
    );

    return (
        <div className="app-container">
            <Topbar />
            <div className="main-wrapper" style={{ background: "#F4F7FE" }}>
                <SideMenu />
                <div className="profile-content-container p-3" style={{ flex: 1, overflow: "auto" }}>
                    <SettingNav />

                    <div className="row g-3 mt-2">
                        {/* LEFT CARD: Tickets */}
                        <div className="col-lg-6">
                            <div className="premium-card h-100">
                                <div className="section-header justify-content-between">
                                    <div className='d-flex'>
                                        <div className="icon-wrap security-icon me-3">
                                            <LuTicket />
                                        </div>
                                        <div>
                                            <h5 className="card-title">Support Tickets</h5>
                                            <p className="card-subtitle">Manage support requests</p>
                                        </div>
                                    </div>
                                    <div>
                                        <AddTicket onTicketCreated={fetchTickets} />
                                    </div>
                                </div>

                                <div className="ticket-list-wrapper">
                                    {loadingTickets ? (
                                        <>
                                            <TicketSkeleton />
                                            <TicketSkeleton />
                                            <TicketSkeleton />
                                        </>
                                    ) : tickets.length > 0 ? (
                                        tickets.map((ticket) => (
                                            <div key={ticket.id} className="ticket-box">
                                                <div className="d-flex justify-content-between align-items-start mb-1">
                                                    <h6 className="ticket-title-text m-0">{ticket.subject}</h6>
                                                    <span className={`status-badge ${ticket.status.toLowerCase().replace(' ', '-')}`}>
                                                        {ticket.status}
                                                    </span>
                                                </div>
                                                <p className="ticket-desc-text">{ticket.description}</p>
                                                {ticket.admin_response && (
                                                    <div className="admin-reply-box mt-2 p-2 bg-light rounded border-start border-primary border-4">
                                                        <small className="fw-bold text-primary">Admin Response:</small>
                                                        <p className="small mb-0">{ticket.admin_response}</p>
                                                    </div>
                                                )}
                                                <div className="d-flex justify-content-between align-items-center mt-2">
                                                    <span className="ticket-meta-text">Created: {new Date(ticket.created_at).toLocaleDateString()}</span>
                                                    <span className="ticket-id-text">#TKT-{ticket.id}</span>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="text-center p-5 text-muted">No support tickets found.</div>
                                    )}
                                </div>

                                <div className="d-flex gap-3 mt-4">
                                    <button className="btn-save-changes d-flex align-items-center gap-2" onClick={fetchTickets}>
                                        <LuHistory /> Refresh List
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* RIGHT CARD: Preferences */}
                        <div className="col-lg-6">
                            <div className="premium-card h-100">
                                <div className="section-header">
                                    <div className="icon-wrap security-icon">
                                        <LuHeadphones />
                                    </div>
                                    <div>
                                        <h5 className="card-title">Support Preferences</h5>
                                        <p className="card-subtitle">How you want to be contacted</p>
                                    </div>
                                </div>

                                {fetchingPrefs ? (
                                    <PreferenceSkeleton />
                                ) : (
                                    <div className="form-body">
                                        <div className="input-block">
                                            <label className="input-label">Preferred Contact Method</label>
                                            <select 
                                                className="form-control-premium form-select"
                                                value={config.contactMethod}
                                                onChange={(e) => handlePrefChange('contactMethod', e.target.value)}
                                            >
                                                <option value="Email">Email</option>
                                                <option value="Phone">Phone</option>
                                                <option value="Dashboard Chat">Dashboard Chat</option>
                                            </select>
                                        </div>

                                        <div className="input-block">
                                            <label className="input-label">Business Hours</label>
                                            <select 
                                                className="form-control-premium form-select"
                                                value={config.businessHours}
                                                onChange={(e) => handlePrefChange('businessHours', e.target.value)}
                                            >
                                                <option>9 AM - 5 PM (UTC)</option>
                                                <option>24/7 Support</option>
                                            </select>
                                        </div>

                                        <div className="input-block">
                                            <label className="input-label">Auto-escalate after</label>
                                            <select 
                                                className="form-control-premium form-select"
                                                value={config.autoEscalate}
                                                onChange={(e) => handlePrefChange('autoEscalate', e.target.value)}
                                            >
                                                <option>24 hours</option>
                                                <option>48 hours</option>
                                                <option>Never</option>
                                            </select>
                                        </div>

                                        <div className="checkbox-list mt-2">
                                            <label className="checkbox-item">
                                                <input 
                                                    type="checkbox" 
                                                    checked={config.emailUpdates}
                                                    onChange={(e) => handlePrefChange('emailUpdates', e.target.checked)}
                                                />
                                                <span className="checkmark"></span>
                                                <span className="checkbox-label">Email me support ticket updates</span>
                                            </label>
                                        </div>
                                    </div>
                                )}

                                <div className="card-footer-actions mt-auto">
                                    <button 
                                        className="btn-save-changes" 
                                        onClick={saveSettings}
                                        disabled={isLoading || fetchingPrefs}
                                    >
                                        {isLoading ? "Saving..." : "Save Preferences"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SupportTicket;